<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from '@/plugins/i18n';
import { SearchArticle } from '@/common/api/models.pleased';
import { useRelatedArticles } from '../api/relatedArticles.api';
import { useRelatedArticlesCustomisation } from '../composables/useRelatedArticlesCustomisation';
import { createTextStyle } from '@/common/customisation/utils/styleUtils';
import { reportingService } from '@/common/reporting';
import { analytics } from '@/common/logs/EventLogger';
import { BTN_EVTS } from '@/directives/trackClick';
import ArticleListItem from '@/components/ArticleListItem.vue';
import { useRouter } from 'vue-router/composables';

interface Props {
  articleId: number;
}

const props = defineProps<Props>();

const router = useRouter();

const { t } = useI18n();

const MAX_ARTICLES = 3;

// Fetch related articles
const { data: articlesData } = useRelatedArticles(computed(() => props.articleId));
const articles = computed(() => articlesData.value || []);

// Customisation
const relatedArticlesCustomisation = useRelatedArticlesCustomisation();

const {
  textColor: articleTextColor,
  borderColor: articleBorderColor,
  backgroundColor: articleBackgroundColor,
} = relatedArticlesCustomisation;

// Styles
const headingStyle = computed(() => createTextStyle(articleTextColor));

// Computed values for ArticleListItem props
const articleTextColorValue = computed(() => articleTextColor.value);
const articleBorderColorValue = computed(() => articleBorderColor.value);
const articleBackgroundColorValue = computed(() => articleBackgroundColor.value);

// Show maximum 3 articles
const showArticles = computed(() => articles.value.slice(0, MAX_ARTICLES));

const handleOpenArticle = (item: SearchArticle) => {
  reportingService.selectedArticleFromRelated(item.id);
  analytics.trackClick({
    name: BTN_EVTS.ARTICLE_RELATED_ARTICLE,
    selection: props.articleId,
    metadata: { relatedArticle: item.id, title: item.title },
  });

  router.push({ name: 'new-article', params: { id: String(item.id) } });
};
</script>

<template>
  <div v-if="articles.length > 0" class="related-articles-container">
    <span class="theme-font-h4" :style="headingStyle">
      {{ t('page.article.related.title') }}
    </span>
    <ul>
      <ArticleListItem
        v-for="item in showArticles"
        :key="item.id"
        :item="item"
        :text-color="articleTextColorValue"
        :background-color="articleBackgroundColorValue"
        :border-color="articleBorderColorValue"
        class="article-item shadow-soft"
        @mousedown="handleOpenArticle(item)"
      />
    </ul>
  </div>
</template>

<style scoped lang="scss">
.related-articles-container {
  display: flex;
  gap: 12px;
  flex-direction: column;
  margin-top: 16px;
}

.article-item {
  margin-bottom: 8px;
}

ul {
  list-style-type: none;
  padding: 0;
  margin: 0;

  :deep(li) {
    margin: 0;
  }
}
</style>
